// frontend/src/components/workflows/StageEditor.tsx - FIXED StageRole references
import React, { useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils';
import { useConfirmation } from '@/components/ui/ConfirmationDialog';
import { useAppStore } from '@/store/app';

// Import types and utilities
import type { 
  WorkflowStage, 
  Group, 
  StageEditorProps, 
  StageRole
} from './types';
import { WorkflowUtils } from './types';

// Stage role options - Sender is excluded as it's auto-managed
const EDITABLE_STAGE_ROLES: Array<{ value: StageRole; label: string; description: string }> = [
  { value: 'Editor', label: 'Editor', description: 'Edits and modifies content' },
  { value: 'Approver', label: 'Approver', description: 'Reviews and approves content' },
  { value: 'Commenter', label: 'Commenter', description: 'Provides comments and feedback' }
];

// Sender role info for display
const SENDER_ROLE = { value: 'Sender', label: 'Sender', description: 'Sends final content to recipients' };

export const StageEditor: React.FC<StageEditorProps> = ({
  stages,
  availableGroups,
  onStageUpdate,
  onStageAdd,
  onStageRemove,
  onStageMove,
  errors = {}
}) => {
  const { confirm } = useConfirmation();
  const { addNotification } = useAppStore();
  // Update stage field with proper field names
  const updateStageField = useCallback((index: number, field: string, value: any) => {
    onStageUpdate(index, { [field]: value });
  }, [onStageUpdate]);

  // Toggle approval groups (using approvalGroups field)
  const toggleApprovalGroup = useCallback((stageIndex: number, groupId: string) => {
    const stage = stages[stageIndex];
    const currentGroups: string[] = stage.approvalGroups || [];
    const newGroups: string[] = currentGroups.includes(groupId)
      ? currentGroups.filter(id => id !== groupId)
      : [...currentGroups, groupId];
    
    onStageUpdate(stageIndex, { approvalGroups: newGroups });
  }, [stages, onStageUpdate]);

  // Get stage role color using WorkflowUtils
  const getStageRoleColor = useCallback((role: StageRole) => {
    return WorkflowUtils.getStageRoleColor(role);
  }, []);

  // Add new stage with proper backend structure
  const handleAddStage = useCallback(() => {
    onStageAdd();
  }, [onStageAdd]);

  // Move stage up/down
  const handleMoveStage = useCallback((index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex >= 0 && newIndex < stages.length) {
      onStageMove(index, newIndex);
    }
  }, [stages.length, onStageMove]);

  // Remove stage with confirmation
  const handleRemoveStage = useCallback(async (index: number) => {
    if (stages.length <= 1) {
      addNotification({ type: 'warning', title: 'Cannot Remove Stage', message: 'At least one stage is required' });
      return;
    }

    const stageName = stages[index]?.stageName || `Stage ${index + 1}`;
    const confirmed = await confirm({
      title: 'Remove Stage',
      message: `Are you sure you want to remove "${stageName}"?`,
      confirmText: 'Remove',
      variant: 'danger',
    });
    if (confirmed) {
      onStageRemove(index);
    }
  }, [stages, onStageRemove, confirm, addNotification]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Workflow Stages ({stages.length})
        </h3>
        <Button
          type="button"
          onClick={handleAddStage}
          size="sm"
          className="flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>Add Stage</span>
        </Button>
      </div>

      {/* No stages message */}
      {stages.length === 0 && (
        <div className="text-center py-8 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No stages configured. Click "Add Stage" to get started.
          </p>
        </div>
      )}

      {/* Stage List */}
      <div className="space-y-4">
        {stages.map((stage, index) => {
          const stageErrors = errors[index] || {};
          const hasErrors = Object.keys(stageErrors).length > 0;
          const isFirst = index === 0;
          const isLast = index === stages.length - 1;

          // Check if this is a protected stage
          const isProtectedFirstEditor = isFirst && stage.role === 'Editor';
          const isProtectedSender = stage.role === 'Sender';

          // First Editor cannot move at all (must stay first)
          // Sender cannot move at all (must stay last)
          const canMoveUp = !isFirst && !isProtectedSender;
          const canMoveDown = !isLast && !isProtectedFirstEditor;
          const canDelete = !isProtectedFirstEditor && !isProtectedSender;

          return (
            <div
              key={`stage-${index}-${stage._id || 'new'}`}
              className={cn(
                'border rounded-lg p-4 transition-all',
                hasErrors
                  ? 'border-red-300 dark:border-red-600 bg-red-50/50 dark:bg-red-900/10'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
              )}
            >
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-full text-sm font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {stage.stageName || `Stage ${index + 1}`}
                    </h4>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={cn(
                        'px-2 py-1 text-xs font-medium rounded-full',
                        getStageRoleColor(stage.role)
                      )}>
                        {stage.role}
                      </span>
                      {stage.mandatory && (
                        <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400 rounded-full">
                          Required
                        </span>
                      )}
                      {(isProtectedFirstEditor || isProtectedSender) && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full flex items-center space-x-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <span>Protected</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stage Controls */}
                <div className="flex items-center space-x-2">
                  {/* Move Up */}
                  {canMoveUp && (
                    <button
                      type="button"
                      onClick={() => handleMoveStage(index, 'up')}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                      title="Move up"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  )}

                  {/* Move Down */}
                  {canMoveDown && (
                    <button
                      type="button"
                      onClick={() => handleMoveStage(index, 'down')}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                      title="Move down"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}

                  {/* Remove Stage */}
                  {canDelete && (
                    <button
                      type="button"
                      onClick={() => handleRemoveStage(index)}
                      disabled={stages.length <= 1}
                      className={cn(
                        'p-1 rounded transition-colors',
                        stages.length <= 1
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                      )}
                      title="Remove stage"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Stage Form */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Stage Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Stage Name *
                    </label>
                    <Input
                      type="text"
                      value={stage.stageName}
                      onChange={(e) => updateStageField(index, 'stageName', e.target.value)}
                      placeholder={`Stage ${index + 1}`}
                      className={stageErrors.stageName ? 'border-red-500 focus:border-red-500' : ''}
                    />
                    {stageErrors.stageName && (
                      <p className="text-red-500 text-xs mt-1">{stageErrors.stageName}</p>
                    )}
                  </div>

                  {/* Stage Role */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Stage Role *
                      {stage.role === 'Sender' && <span className="text-xs font-normal text-gray-500"> (Auto-managed)</span>}
                      {index === 0 && stage.role === 'Editor' && <span className="text-xs font-normal text-gray-500"> (Required first stage)</span>}
                    </label>
                    {stage.role === 'Sender' || (index === 0 && stage.role === 'Editor') ? (
                      // Protected stage - read-only display
                      <div className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 flex items-center justify-between">
                        <span>
                          {stage.role === 'Sender'
                            ? `${SENDER_ROLE.label} - ${SENDER_ROLE.description}`
                            : `Editor - Edits and modifies content`
                          }
                        </span>
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                    ) : (
                      // Regular stage - editable dropdown
                      <select
                        value={stage.role}
                        onChange={(e) => updateStageField(index, 'role', e.target.value as StageRole)}
                        className={cn(
                          'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md',
                          'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white',
                          stageErrors.role ? 'border-red-500 focus:border-red-500' : ''
                        )}
                      >
                        {EDITABLE_STAGE_ROLES.map((roleOption) => (
                          <option key={roleOption.value} value={roleOption.value}>
                            {roleOption.label} - {roleOption.description}
                          </option>
                        ))}
                      </select>
                    )}
                    {stageErrors.role && (
                      <p className="text-red-500 text-xs mt-1">{stageErrors.role}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={stage.description || ''}
                      onChange={(e) => updateStageField(index, 'description', e.target.value)}
                      placeholder="Describe what happens in this stage..."
                      rows={2}
                      className={cn(
                        'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md',
                        'focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white resize-none',
                        stageErrors.description ? 'border-red-500 focus:border-red-500' : ''
                      )}
                    />
                    {stageErrors.description && (
                      <p className="text-red-500 text-xs mt-1">{stageErrors.description}</p>
                    )}
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4">
                  {/* Approval Groups */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Approval Groups
                    </label>
                    {availableGroups.length > 0 ? (
                      <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-2">
                        {availableGroups.map(group => {
                          const groupId = group._id || group.id || '';
                          const isSelected = (stage.approvalGroups || []).includes(groupId);
                          
                          return (
                            <div key={groupId} className="flex items-center">
                              <input
                                type="checkbox"
                                id={`stage-${index}-group-${groupId}`}
                                checked={isSelected}
                                onChange={() => toggleApprovalGroup(index, groupId)}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              <label 
                                htmlFor={`stage-${index}-group-${groupId}`}
                                className="ml-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer"
                              >
                                {group.name}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 p-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-md">
                        No groups available
                      </div>
                    )}
                  </div>

                  {/* Time Limit and Required Approvals */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Time Limit (hours)
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="720"
                        value={stage.timeLimit?.hours || ''}
                        onChange={(e) => updateStageField(index, 'timeLimit', 
                          e.target.value ? { hours: parseInt(e.target.value) || 1 } : undefined
                        )}
                        placeholder="24"
                        className={stageErrors.timeLimit ? 'border-red-500 focus:border-red-500' : ''}
                      />
                      {stageErrors.timeLimit && (
                        <p className="text-red-500 text-xs mt-1">{stageErrors.timeLimit}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Required Approvals
                      </label>
                      <Input
                        type="number"
                        min="1"
                        max="10"
                        value={stage.requiredApprovals || 1}
                        onChange={(e) => updateStageField(index, 'requiredApprovals', parseInt(e.target.value) || 1)}
                        className={stageErrors.requiredApprovals ? 'border-red-500 focus:border-red-500' : ''}
                      />
                      {stageErrors.requiredApprovals && (
                        <p className="text-red-500 text-xs mt-1">{stageErrors.requiredApprovals}</p>
                      )}
                    </div>
                  </div>

                  {/* Stage Options */}
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`stage-${index}-mandatory`}
                        checked={stage.mandatory || false}
                        onChange={(e) => updateStageField(index, 'mandatory', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`stage-${index}-mandatory`}
                        className="ml-2 text-xs text-gray-700 dark:text-gray-300"
                      >
                        Mandatory
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`stage-${index}-parallel`}
                        checked={stage.allowParallel || false}
                        onChange={(e) => updateStageField(index, 'allowParallel', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`stage-${index}-parallel`}
                        className="ml-2 text-xs text-gray-700 dark:text-gray-300"
                      >
                        Allow Parallel Processing
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`stage-${index}-auto-advance`}
                        checked={stage.autoAdvance || false}
                        onChange={(e) => updateStageField(index, 'autoAdvance', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label
                        htmlFor={`stage-${index}-auto-advance`}
                        className="ml-2 text-xs text-gray-700 dark:text-gray-300"
                      >
                        Auto Advance
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stage Info Footer */}
              {(stage.timeLimit || stage.requiredApprovals) && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                    {stage.timeLimit && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{stage.timeLimit.hours}h limit</span>
                      </div>
                    )}
                    {stage.requiredApprovals && stage.requiredApprovals > 1 && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{stage.requiredApprovals} approvals needed</span>
                      </div>
                    )}
                    {stage.allowParallel && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Parallel processing</span>
                      </div>
                    )}
                    {stage.autoAdvance && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                        <span>Auto advance</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Stage Flow Visualization */}
      {stages.length > 1 && (
        <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Workflow Flow
          </h4>
          <div className="flex items-center space-x-2 overflow-x-auto pb-2">
            {stages.map((stage, index) => (
              <React.Fragment key={`flow-${index}`}>
                <div className="flex-shrink-0 flex flex-col items-center space-y-1">
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium',
                    getStageRoleColor(stage.role)
                  )}>
                    {index + 1}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 text-center max-w-16 truncate">
                    {stage.stageName || `Stage ${index + 1}`}
                  </div>
                </div>
                {index < stages.length - 1 && (
                  <div className="flex-shrink-0 w-8 flex justify-center">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Stage Summary */}
      {stages.length > 0 && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
            Workflow Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="text-blue-800 dark:text-blue-300">
              <div className="font-medium">{stages.length}</div>
              <div className="text-xs">Total Stages</div>
            </div>
            <div className="text-blue-800 dark:text-blue-300">
              <div className="font-medium">{stages.filter(s => s.mandatory).length}</div>
              <div className="text-xs">Mandatory</div>
            </div>
            <div className="text-blue-800 dark:text-blue-300">
              <div className="font-medium">{stages.filter(s => s.allowParallel).length}</div>
              <div className="text-xs">Parallel</div>
            </div>
            <div className="text-blue-800 dark:text-blue-300">
              <div className="font-medium">
                {stages.reduce((total, s) => total + (s.timeLimit?.hours || 24), 0)}h
              </div>
              <div className="text-xs">Est. Duration</div>
            </div>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
        <p><strong>Tips:</strong></p>
        <ul className="list-disc list-inside space-y-1">
          <li>Use descriptive stage names that clearly indicate the action being performed</li>
          <li>Approver stages require one or more users to approve before proceeding</li>
          <li>Editor stages allow users to modify content before it proceeds</li>
          <li>Sender stages are typically the final step where content is sent to recipients</li>
          <li>Commenter stages allow users to provide feedback without blocking progress</li>
          <li>Time limits help ensure workflows don't stall indefinitely</li>
          <li>Parallel processing allows multiple users to work on the same stage simultaneously</li>
        </ul>
      </div>
    </div>
  );
};